import { availableSeats } from '@/lib/billing/advisorTrial';
import { useState } from 'react';
import { logger } from '@/lib/logger';
/**
 * useAdvisorProfile - Hook for fetching and managing advisor profile data
 *
 * Provides:
 * - Advisor profile (profession, firm, verification status)
 * - Seat balance (available, used, pending)
 * - Seat assignments (clients)
 * - Team members
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';

export interface AdvisorProfile {
  id: string;
  user_id: string;
  full_name: string;
  profession_type: string;
  license_number: string | null;
  license_state: string | null;
  firm_name: string | null;
  firm_website: string | null;
  years_experience: number | null;
  specializations: string[] | null;
  states_practicing: string[] | null;
  verification_status: 'unverified' | 'self_attested' | 'pending' | 'verified' | 'rejected';
  verified_at: string | null;
  stripe_customer_id: string | null;
  billing_email: string | null;
  billing_company: string | null;
  created_at: string;
  updated_at: string;
  // Team member fields (only set when user is a team member, not primary advisor)
  is_team_member?: boolean;
  team_role?: string;
  parent_advisor_id?: string;
}

export interface SeatBalance {
  id: string;
  advisor_id: string;
  total_purchased: number;
  total_active: number;
  total_pending: number;
  total_returned: number;
  available: number;
  updated_at: string;
}

export interface SeatAssignment {
  id: string;
  advisor_id: string;
  case_draft_id: string | null;
  client_user_id: string | null;
  client_email: string;
  status: 'pending' | 'active' | 'expired' | 'declined' | 'revoked';
  assigned_at: string;
  activated_at: string | null;
  expires_at: string | null;
  // Joined data
  client_profile?: {
    full_name: string | null;
    email: string | null;
  };
  case_draft?: {
    client_name: string;
    state: string | null;
    situation_type: string | null;
  };
}

export interface CaseDraft {
  id: string;
  advisor_id: string;
  client_name: string;
  client_email: string;
  state: string | null;
  situation_type: string | null;
  complexity_estimate: string | null;
  key_issues: string[] | null;
  spouse_name: string | null;
  marriage_duration_years: number | null;
  children_count: number | null;
  has_prenup: boolean | null;
  advisor_notes: string | null;
  internal_reference: string | null;
  invitation_token: string;
  invitation_sent_at: string | null;
  invitation_status: 'draft' | 'sent' | 'accepted' | 'expired' | 'cancelled';
  client_user_id: string | null;
  converted_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useAdvisorProfile() {
  
  const { user } = useAuth();

  return useQuery({
    queryKey: ['advisor-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // First, try to get the user's own advisor profile
      // Use maybeSingle to avoid PGRST116 errors
      const { data, error } = await (supabase as any)
        .from('advisor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        trackSupabaseError('select', 'advisor_profiles', error);
        throw error;
      }

      if (data) {
        return data as AdvisorProfile;
      }

      // No own profile - check if user is a team member
      const { data: teamMembership } = await (supabase as any)
        .from('advisor_team_members')
        .select(`
          id,
          advisor_id,
          role,
          access_type,
          status,
          advisor_name,
          advisor_firm
        `)
        .eq('member_user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (teamMembership) {
        // Get the parent advisor's full profile
        const { data: parentProfile } = await (supabase as any)
          .from('advisor_profiles')
          .select('*')
          .eq('id', teamMembership.advisor_id)
          .maybeSingle();

        if (parentProfile) {
          // Return parent profile with team member flag
          return {
            ...parentProfile,
            is_team_member: true,
            team_role: teamMembership.role,
            parent_advisor_id: teamMembership.advisor_id,
          } as AdvisorProfile;
        }
      }

      // Not an advisor and not a team member
      return null;
    },
    enabled: !!user?.id,
  });
}

// Helper function to get advisor ID (own profile or parent advisor for team members)
async function getAdvisorId(supabaseClient: any, userId: string): Promise<string | null> {
  // First check for own profile - use maybeSingle to avoid PGRST116 errors
  const { data: ownProfile } = await supabaseClient
    .from('advisor_profiles')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (ownProfile) return ownProfile.id;

  // Check for team membership
  const { data: teamMembership } = await supabaseClient
    .from('advisor_team_members')
    .select('advisor_id')
    .eq('member_user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  return teamMembership?.advisor_id || null;
}

export function useSeatBalance() {
  
  const { user } = useAuth();

  return useQuery({
    queryKey: ['advisor-seat-balance', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get advisor ID (own or parent's)
      const advisorId = await getAdvisorId(supabase, user.id);
      if (!advisorId) return null;

      // For reference in the balance object
      const profile = { id: advisorId };

      // Use maybeSingle to avoid PGRST116 errors
      const { data, error } = await (supabase as any)
        .from('advisor_seat_balance')
        .select('*')
        .eq('advisor_id', profile.id)
        .maybeSingle();

      if (error) {
        trackSupabaseError('select', 'advisor_seat_balance', error);
        throw error;
      }

      /*
        A subscription is a seat ledger too, and this hook used to ignore it. A brand-new firm has no
        advisor_seat_balance row, so `available` came back 0 and every gate reading this hook — Send
        Invitation on add-client, most visibly — stayed disabled while the signup page promised a 14-day
        trial. The firm's first experience of the product was a paywall contradicting the sentence it had
        just read (#1868). availableSeats() decides which ledger answers; see its note on why they are not
        additive.
      */
      const { data: sub } = await (supabase as any)
        .from('advisor_subscriptions')
        .select('tier, status, seats_used')
        .eq('advisor_id', profile.id)
        .in('status', ['trialing', 'active', 'past_due'])
        .maybeSingle();

      const seats = availableSeats({
        subscription: sub ? { status: sub.status, tier: sub.tier, seats_used: sub.seats_used } : null,
        legacy: data
          ? { total_purchased: data.total_purchased, total_active: data.total_active, total_pending: data.total_pending }
          : null,
      });

      return {
        id: data?.id ?? '',
        advisor_id: profile.id,
        total_purchased: seats.limit,
        total_active: data?.total_active ?? seats.used,
        total_pending: data?.total_pending ?? 0,
        total_returned: data?.total_returned ?? 0,
        available: seats.available,
        updated_at: data?.updated_at ?? new Date().toISOString(),
      } as SeatBalance;
    },
    enabled: !!user?.id,
  });
}

export function useSeatAssignments() {
  
  const { user } = useAuth();

  return useQuery({
    queryKey: ['advisor-seat-assignments', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get advisor ID (own or parent's for team members)
      const advisorId = await getAdvisorId(supabase, user.id);
      if (!advisorId) return [];

      const profile = { id: advisorId };

      const { data, error } = await (supabase as any)
        .from('advisor_seat_assignments')
        .select(`
          *,
          advisor_case_drafts(client_name, state, situation_type)
        `)
        .eq('advisor_id', profile.id)
        .order('assigned_at', { ascending: false });

      if (error) {
        trackSupabaseError('select', 'advisor_seat_assignments', error);
        throw error;
      }

      // Fetch client profiles separately for active assignments
      const assignments = (data || []) as unknown as SeatAssignment[];
      const clientUserIds = assignments
        .filter((a) => a.client_user_id)
        .map((a) => a.client_user_id as string);

      if (clientUserIds.length > 0) {
        const { data: profiles } = await (supabase as any)
          .from('profiles')
          .select('id, full_name, email')
          .in('id', clientUserIds);

        const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

        return assignments.map((a) => ({
          ...a,
          client_profile: a.client_user_id ? profileMap.get(a.client_user_id) : undefined,
          case_draft: (a as any).advisor_case_drafts,
        }));
      }

      return assignments.map((a) => ({
        ...a,
        case_draft: (a as any).advisor_case_drafts,
      }));
    },
    enabled: !!user?.id,
  });
}

export function useCaseDrafts() {
  
  const { user } = useAuth();

  return useQuery({
    queryKey: ['advisor-case-drafts', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get advisor ID (own or parent's for team members)
      const advisorId = await getAdvisorId(supabase, user.id);
      if (!advisorId) return [];

      const profile = { id: advisorId };

      const { data, error } = await (supabase as any)
        .from('advisor_case_drafts')
        .select('*')
        .eq('advisor_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) {
        trackSupabaseError('select', 'advisor_case_drafts', error);
        throw error;
      }

      return (data || []) as CaseDraft[];
    },
    enabled: !!user?.id,
  });
}

export function useCreateCaseDraft() {
  
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (draft: Partial<CaseDraft>) => {
      if (!user?.id) throw new Error('Not authenticated');

      // Get advisor ID - only primary advisors can create drafts
      // Team members should not create case drafts
      const { data: profile } = await (supabase as any)
        .from('advisor_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) throw new Error('Only primary advisors can create case drafts');

      const { data, error } = await (supabase as any)
        .from('advisor_case_drafts')
        .insert({
          advisor_id: profile.id,
          client_name: draft.client_name!,
          client_email: draft.client_email!,
          state: draft.state,
          situation_type: draft.situation_type,
          complexity_estimate: draft.complexity_estimate,
          key_issues: draft.key_issues,
          spouse_name: draft.spouse_name,
          marriage_duration_years: draft.marriage_duration_years,
          children_count: draft.children_count,
          has_prenup: draft.has_prenup,
          advisor_notes: draft.advisor_notes,
          internal_reference: draft.internal_reference,
        })
        .select()
        .single();

      if (error) {
        trackSupabaseError('insert', 'advisor_case_drafts', error);
        throw error;
      }
      return data as CaseDraft;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-case-drafts'] });
    },
  });
}

export function useAssignSeatToClient() {
  
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      caseDraftId,
      clientEmail,
    }: {
      caseDraftId: string;
      clientEmail: string;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');

      // Get advisor profile ID - only primary advisors can assign seats
      const { data: profile } = await (supabase as any)
        .from('advisor_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) throw new Error('Only primary advisors can assign seats');

      // Call the database function to assign seat
      const { data, error } = await (supabase as any).rpc('assign_seat_to_client', {
        p_advisor_id: profile.id,
        p_case_draft_id: caseDraftId,
        p_client_email: clientEmail,
      });

      if (error) {
        trackSupabaseError('select', 'assign_seat_to_client', error);
        throw error;
      }
      return data as string; // Returns assignment ID
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-seat-balance'] });
      queryClient.invalidateQueries({ queryKey: ['advisor-seat-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['advisor-case-drafts'] });
    },
  });
}
